import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layers, Clock, BookAudio, Check, Loader2 } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { formatDuration } from "../mock";
import BookCover from "../components/BookCover";
import { Button } from "../components/ui/button";

/**
 * A series, in reading order.
 *
 * The point is the order and what you already own: someone who has finished
 * book two is here to find book three, and the page should answer that at a
 * glance rather than making them read seven titles to work out where they are.
 */
export default function SeriesPage() {
  const { name } = useParams();
  const series = decodeURIComponent(name || "");
  const { user, hasBook } = useAuth();
  const [books, setBooks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/series/${encodeURIComponent(series)}`)
      .then((r) => { if (!cancelled) setBooks(r.data?.books || []); })
      .catch(() => { if (!cancelled) setBooks([]); });
    return () => { cancelled = true; };
  }, [series]);

  if (books === null) {
    return (
      <div className="py-32 flex justify-center" role="status" aria-label="Loading">
        <Loader2 className="w-6 h-6 text-muted-foreground/60 animate-spin" />
      </div>
    );
  }

  const owned = books.filter((b) => user && hasBook(b.id)).length;
  const hours = Math.round(books.reduce((s, b) => s + (b.duration || 0), 0) / 3600);
  // The first volume they do not have is the one they came here for.
  const next = books.find((b) => !(user && hasBook(b.id)));

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-float-up min-h-[70vh]">
      <div className="flex items-start gap-5 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0">
          <Layers className="w-7 h-7 text-brand-950" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-brand-400 font-semibold">Series</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{series}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <BookAudio className="w-4 h-4" /> {books.length} audiobooks
            </span>
            {hours > 0 && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {formatDuration(hours * 3600)} in total
              </span>
            )}
            {user && owned > 0 && (
              <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
                <Check className="w-4 h-4" /> {owned} of {books.length} in your library
              </span>
            )}
          </div>
        </div>
      </div>

      {user && next && owned > 0 && (
        <div className="rounded-2xl border border-brand-500/25 bg-brand-500/5 p-5 mb-8 flex flex-wrap items-center gap-4">
          <BookCover book={next} className="w-12 h-[72px] rounded-md cover-shadow shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-widest text-brand-400 font-semibold">Next in the series</p>
            <p className="font-semibold truncate mt-0.5">{next.title_en}</p>
          </div>
          <Button asChild className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full px-6 shrink-0">
            <Link to={`/book/${next.slug || next.id}`}>Continue the story</Link>
          </Button>
        </div>
      )}

      <ol className="space-y-3">
        {books.map((b, i) => {
          const mine = user && hasBook(b.id);
          return (
            <li key={b.id}>
              <div className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card/50 hover:border-brand-500/40 transition-colors group">
                <span className="w-8 shrink-0 text-center font-display text-lg font-semibold text-muted-foreground tabular-nums">
                  {b.seriesPosition ?? i + 1}
                </span>
                <Link to={`/book/${b.slug || b.id}`} className="shrink-0" aria-label={b.title_en}>
                  <BookCover book={b} className="w-14 h-20 rounded-lg cover-shadow" />
                </Link>
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold truncate">
                    <Link to={`/book/${b.slug || b.id}`} className="group-hover:text-brand-400 transition-colors">
                      {b.title_en}
                    </Link>
                  </h2>
                  <p className="text-sm text-muted-foreground truncate">
                    <Link to={`/narrator/${encodeURIComponent(b.narrator)}`} className="hover:text-brand-400 transition-colors">
                      {b.narrator}
                    </Link>
                    {" · "}{formatDuration(b.duration)}
                  </p>
                </div>
                {mine ? (
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold shrink-0">
                    <Check className="w-3.5 h-3.5" /> In your library
                  </span>
                ) : (
                  <Button asChild variant="outline" className="rounded-full shrink-0 border-white/25 bg-white/5 hover:bg-white/10">
                    <Link to={`/book/${b.slug || b.id}`}>£{Number(b.price).toFixed(2)}</Link>
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
